/**
 * Sign-in through Supabase Auth only (D38). @supabase/auth-js is the auth client on its own — it has no
 * database methods, so the browser cannot reach Postgres even by mistake. All data goes through /api.
 */
import type { Session } from '@supabase/auth-js';
import { AuthClient } from '@supabase/auth-js';

type Client = InstanceType<typeof AuthClient>;
import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { markEidSignUp } from './eid-sign-up.ts';
import { rememberSignInReturn } from './sign-in-return.ts';

export interface PublicConfig {
  supabaseUrl: string;
  supabaseAnonKey: string;
  authProviders: ('google' | 'azure')[];
  /** The eIDs offered here (0127); absent or empty while the switch is off. */
  eid?: ('vipps' | 'bankid')[];
}

type AuthState =
  | { status: 'loading' }
  | { status: 'unconfigured' }
  /** `because` is the failure itself, so the screen can tell *offline* from *the server is not answering*. */
  | { status: 'error'; because: unknown }
  | { status: 'signed-out'; config: PublicConfig; client: Client }
  | { status: 'signed-in'; config: PublicConfig; client: Client; session: Session };

/**
 * What a sign-in attempt came back as. A failure carries the two things that decide what to say about it:
 * the status, and Supabase's own error code — `over_email_send_rate_limit` being the one a person meets
 * by pressing the button twice.
 */
export type SignInOutcome = { ok: true } | { ok: false; status: number; code: string };

interface AuthContextValue {
  state: AuthState;
  getToken: () => Promise<string | null>;
  /**
   * **The reason comes back, not only the verdict** (F202). This returned `{ ok }` and the screen had one
   * sentence for every way it could fail — including the ordinary one, *you asked for another link a
   * moment ago*, which the person can act on and which reads as a fault in the product.
   */
  signInWithEmail: (email: string) => Promise<SignInOutcome>;
  signInWithProvider: (provider: 'google' | 'azure') => Promise<void>;
  /**
   * **Signing in with an eID** (0127): the browser goes to the API, which sends it on to the provider with the flow
   * sealed in a cookie. The provider sends it back to `/eid/{provider}/return`, which completes it.
   */
  signInWithEid: (provider: 'vipps' | 'bankid') => void;
  /**
   * The provider's answer, handed to the API for Supabase Auth's own session; a refusal comes back as its code, and
   * an eID nobody holds yet as a sign-up waiting for its email, with the name it verified (D51 row 8).
   */
  completeEidSignIn: (
    provider: 'vipps' | 'bankid',
    answer: { code?: string; state?: string; error?: string },
  ) => Promise<{ ok: true } | { ok: false; code: string } | { signUp: { name: string } }>;
  /**
   * **A BankID sign-up's email** (D51 row 8): the same link as signing in with email, after marking in this browser
   * that a sign-up waits for it, so the signed-in app finishes it when the link brings the person back.
   */
  signUpWithEmail: (email: string) => Promise<SignInOutcome>;
  signOut: () => Promise<void>;
  /**
   * **Sign in again, for an app that asks for a recent sign-in** (F554): this browser's session ends and nothing else,
   * so apps signed in with Autho and other devices stay signed in. The sign-in screen shows where the person is, and
   * the consent screen they came from is gone back to once they are in (`return-to.ts`).
   */
  signInAgain: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** What an eID flow was started for, kept in this tab only until the provider sends the person back. */
const EID_INTENT = 'autho.eid.intent';
export function rememberEidIntent(intent: 'signin' | 'link') {
  try {
    window.sessionStorage.setItem(EID_INTENT, intent);
  } catch {
    // A private window may refuse storage; the return page then treats the answer as a sign-in.
  }
}
export function eidIntent(): 'signin' | 'link' {
  try {
    return window.sessionStorage.getItem(EID_INTENT) === 'link' ? 'link' : 'signin';
  } catch {
    return 'signin';
  }
}

/**
 * **One person's answers never reach the next** (isolation finding 10, F389). The query cache is keyed by profile and
 * resource, not by person, so after a sign-out in a shared tab the next person to sign in was shown the previous
 * person's cached answers until each one refetched. The cache is now emptied whenever a signed-in person stops being
 * the one signed in — signed out, or replaced by somebody else; the same person's session refreshing keeps it. A
 * sign-in from nobody clears nothing: the sign-out before it already did, and a screen being shown at that moment
 * keeps the queries it is watching.
 */
export function cacheFollowsThePerson(queryClient: Pick<QueryClient, 'clear'>) {
  let seen: string | null = null;
  return (userId: string | null) => {
    if (seen !== null && seen !== userId) queryClient.clear();
    seen = userId;
  };
}

async function loadConfig(): Promise<PublicConfig | null> {
  const res = await fetch('/api/config', { headers: { accept: 'application/json' } });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`config ${res.status}`);
  return (await res.json()) as PublicConfig;
}

/**
 * **Signing out of Autho signs the person out of the apps they signed in to with it** (F494): the API ends every
 * session, grant, code and token its OpenID provider keeps for them. Best effort, like the rest of a sign-out: it never
 * stops one.
 */
export async function endAuthoSignIns(token: string | null, fetcher: typeof fetch): Promise<void> {
  if (token === null) return;
  try {
    await fetcher('/api/me/oidc/sign-out', {
      method: 'POST',
      headers: { authorization: `Bearer ${token}` },
    });
  } catch {
    // Ignored on purpose: a sign-out goes ahead whatever the API answered.
  }
}

/**
 * **Signing out: everywhere, and it cannot fail** (F495, after F203).
 *
 * It revokes the session on the server, in every browser it was copied to (`scope: 'global'`): a refresh token taken
 * from this one stops working at its next refresh. What it cannot reach is an access token already handed out, which
 * Supabase signs for an hour and the API checks without asking anyone; that hour is the residual window, written down
 * in F495.
 *
 * The app also signs itself out when the server will not accept the session (F203), which is exactly when a global
 * sign-out cannot work: it presents the same dead token to `/auth/v1/logout`, and the server answers **403
 * `bad_jwt`**. So a refused global sign-out falls back to the local one, and the storage key is removed by hand
 * whatever the client did. Recovery from a session nobody can verify never depends on that session being accepted
 * anywhere, and a token the server rejects is already worthless.
 */
export async function signOutEverywhere(
  client: Pick<InstanceType<typeof AuthClient>, 'signOut'> | null,
  storage: () => Pick<Storage, 'removeItem'>,
): Promise<void> {
  try {
    const { error } = (await client?.signOut({ scope: 'global' })) ?? { error: null };
    if (error) await client?.signOut({ scope: 'local' });
  } catch {
    try {
      await client?.signOut({ scope: 'local' });
    } catch {
      // Ignored on purpose: the removal below is what has to happen.
    }
  }
  try {
    storage().removeItem('autho.auth');
  } catch {
    // Storage can throw in a private window; there is nothing further to do.
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });
  const queryClient = useQueryClient();
  const follow = useRef(cacheFollowsThePerson(queryClient));

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;
    loadConfig()
      .then(async (config) => {
        if (cancelled) return;
        if (!config) return setState({ status: 'unconfigured' });
        const client = new AuthClient({
          url: `${config.supabaseUrl.replace(/\/$/, '')}/auth/v1`,
          headers: { apikey: config.supabaseAnonKey },
          storageKey: 'autho.auth',
          autoRefreshToken: true,
          persistSession: true,
          detectSessionInUrl: true,
          flowType: 'pkce',
        });
        const apply = (session: Session | null) => {
          follow.current(session?.user.id ?? null);
          setState(
            session
              ? { status: 'signed-in', config, client, session }
              : { status: 'signed-out', config, client },
          );
        };
        const { data } = client.onAuthStateChange((_event, session) => apply(session));
        unsubscribe = () => data.subscription.unsubscribe();
        const { data: current } = await client.getSession();
        if (!cancelled) apply(current.session);
      })
      .catch((because: unknown) => {
        if (!cancelled) setState({ status: 'error', because });
      });
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const client =
      state.status === 'signed-in' || state.status === 'signed-out' ? state.client : null;
    /** A sign-in link to an address, the one way an email signs in, whether signing in or finishing a sign-up. */
    const sendLink = async (email: string): Promise<SignInOutcome> => {
      // No client at all: the app was served without a Supabase URL, which is a misconfiguration and
      // not something the person did.
      if (!client) return { ok: false, status: 0, code: 'not_configured' };
      const { error } = await client.signInWithOtp({
        email,
        options: { emailRedirectTo: window.location.origin, shouldCreateUser: true },
      });
      // `status` and `code` are what Supabase's own error carries, and they are the whole difference
      // between *wait a moment* and *this one is ours*.
      return error
        ? { ok: false as const, status: error.status ?? 0, code: error.code ?? '' }
        : { ok: true as const };
    };
    return {
      state,
      async getToken() {
        if (!client) return null;
        const { data } = await client.getSession();
        return data.session?.access_token ?? null;
      },
      signInWithEmail: sendLink,
      async signUpWithEmail(email) {
        markEidSignUp();
        return sendLink(email);
      },
      async signInWithProvider(provider) {
        if (!client) return;
        // The provider sends the person back to the origin, the one address Supabase's exact list holds; the page
        // they were on is kept in this tab and opened when they are back (row 14).
        // The path alone: a query or a fragment can carry a code or a state, and is never kept (the Mac's review).
        rememberSignInReturn(provider, window.location.pathname);
        await client.signInWithOAuth({ provider, options: { redirectTo: window.location.origin } });
      },
      signInWithEid(provider) {
        rememberEidIntent('signin');
        window.location.assign(`/auth/eid/${provider}/start`);
      },
      async completeEidSignIn(provider, answer) {
        if (!client) return { ok: false, code: 'not_configured' };
        const res = await fetch(`/auth/eid/${provider}/complete`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(answer),
        }).catch(() => null);
        if (res === null) return { ok: false, code: 'offline' };
        const body = (await res.json().catch(() => ({}))) as {
          accessToken?: string;
          refreshToken?: string;
          signUp?: { name?: string };
          error?: { code?: string };
        };
        if (res.ok && typeof body.signUp?.name === 'string')
          return { signUp: { name: body.signUp.name } };
        if (!res.ok || !body.accessToken || !body.refreshToken)
          return { ok: false, code: body.error?.code ?? `status_${res.status}` };
        // Supabase Auth's own session, minted for this person by the API: from here on, exactly as any sign-in.
        const { error } = await client.setSession({
          access_token: body.accessToken,
          refresh_token: body.refreshToken,
        });
        return error ? { ok: false, code: error.code ?? 'session' } : { ok: true };
      },
      async signInAgain() {
        await client?.signOut({ scope: 'local' });
        queryClient.clear();
      },
      /** Everywhere, and it cannot fail: {@link signOutEverywhere}. */
      async signOut() {
        // First the apps signed in with Autho (F494), while this session can still say who is signing out.
        const token = client
          ? ((await client.getSession()).data.session?.access_token ?? null)
          : null;
        await endAuthoSignIns(token, fetch);
        await signOutEverywhere(client, () => window.localStorage);
        // And whatever this person was shown goes with them, whether or not the client announced the change.
        queryClient.clear();
      },
    };
  }, [state, queryClient]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
